import { ContentLayout } from '../Layouts/ContentLayout/ContentLayout';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { DepositorWiresList } from './ui/DepositorWiresList';
import { NoResults } from '@/components/NoResults/NoResults';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import NoTransactionsIcon from '../../images/NoTransactionsIcon.svg?react';
import { loadDepositorWires } from '@/bundle/_Depositor/api/api';
import { AdBanner } from '@/bundle/shared/components/banner/AdBanner/AdBanner';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { useHasAnyOrganizationAdminRole } from '@/bundle/shared/hooks/useHasAnyOrganizationAdminRole';
import { Box } from '@/components/Box/Box';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { NoSearchResults } from '../shared/components/NoSearchResults';
import { useSearch } from '../shared/hooks/useSearch';
import { getSearchListData } from '@/helpers/helpers';

export const DepositorWiresPage = () => {
  const { search, setSearch, clearSearch, searchParams, submitSearch } = useSearch<{ name: string }>('name');
  const { hasAnyOrganizationAdminRole } = useHasAnyOrganizationAdminRole();

  const { data, isPending } = useQuery({
    queryKey: ['load_depositor_wires', searchParams],
    queryFn: () => loadDepositorWires(searchParams),
    placeholderData: keepPreviousData,
  });

  const handleSubmitSearch = () => {
    if (!!search.length && searchParams.name !== search) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.DepositorSearchSubmit);
    }

    submitSearch();
  };

  const { list, hasData, totalCount, hasPagination, noResults, noSearchResults } = getSearchListData(
    data?.body,
    searchParams,
    isPending
  );

  return (
    <ContentLayout
      title='Wires'
      breadcrumbs='Wires'
      subtitle='Please view the wires you have been assigned to. Select a wire to view more details.'
      banner={hasAnyOrganizationAdminRole ? null : <AdBanner />}
      offsetSize='small'
    >
      <PageBody>
        <Box display='flex' alignItems='flex-start' columnGap='20px' flexWrap='wrap'>
          <Box width='360px' mobile={{ width: '100%' }}>
            <SearchInput
              placeholder='Search by wire name'
              value={search}
              onChange={(value) => setSearch(value)}
              onClear={clearSearch}
              onSubmit={handleSubmitSearch}
            />
          </Box>
        </Box>
        {hasData && <DepositorWiresList list={list} />}
        {noResults && <NoResults icon={<NoTransactionsIcon />} message='There is no wire yet.' />}
        {noSearchResults && <NoSearchResults />}
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams?.page} limit={searchParams?.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
